/**
 * useSkuNotify — hook providing a notifySku(skuCode) function that POSTs to
 * POST /api/v1/sku-notify. Throws on non-2xx so the caller (e.g. SkuLockedTeaser)
 * can handle error state.
 *
 * Reads the JWT from the auth store. If no JWT is present, throws immediately
 * (the auth gate upstream should have prevented reaching this state).
 */

import { useCallback } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export class SkuNotifyError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'SkuNotifyError';
  }
}

export function useSkuNotify(): { notifySku: (skuCode: string) => Promise<void> } {
  const jwt = useAuth((s) => s.jwt);

  const notifySku = useCallback(
    async (skuCode: string): Promise<void> => {
      if (!jwt) throw new SkuNotifyError(401, 'not_authenticated');

      const res = await apiFetch('/api/v1/sku-notify', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ skuCode }),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new SkuNotifyError(res.status, `sku_notify_failed: ${res.status} ${body}`);
      }
    },
    [jwt],
  );

  return { notifySku };
}
